import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const ModernFarmhouseADUDesignSanMateo = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Modern Farmhouse ADU Design & Construction in San Mateo",
    "description": "Create stunning Modern Farmhouse ADUs in San Mateo blending rustic charm with contemporary comfort and open living spaces.",
    "url": "https://www.udaadu.com/modern-farmhouse-adu-design-san-mateo",
    "mainEntity": {
      "@type": "Service",
      "name": "Modern Farmhouse ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "San Mateo, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/modern-farmhouse-adu-design-san-mateo" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Modern Farmhouse ADU Design & Construction in San Mateo
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Create stunning Modern Farmhouse ADUs in San Mateo blending rustic charm with contemporary comfort and open living spaces.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Modern Farmhouse ADUs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Why Choose Modern Farmhouse ADUs in San Mateo?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Perfect Balance</h3>
              <p className="text-muted-foreground">
                Modern Farmhouse style perfectly balances rustic charm with contemporary amenities, ideal for San Mateo's suburban family lifestyle.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Timeless Appeal</h3>
              <p className="text-muted-foreground">
                This style transcends trends, offering enduring aesthetic appeal that complements both traditional and modern San Mateo neighborhoods.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Flexible Design</h3>
              <p className="text-muted-foreground">
                Open floor plans and versatile spaces adapt to changing needs, perfect for home offices, guest quarters, or rental income.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Modern Farmhouse ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Exterior Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Board and batten or shiplap siding</li>
                <li>• Metal roofing with clean lines</li>
                <li>• Large windows with black trim</li>
                <li>• Covered porches and outdoor living areas</li>
                <li>• Mixed materials: wood, stone, and metal</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Interior Design</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Open-concept living with vaulted ceilings</li>
                <li>• Exposed beams and shiplap accent walls</li>
                <li>• Farmhouse-style kitchens with islands</li>
                <li>• Barn doors and rustic hardware</li>
                <li>• Neutral color palettes with natural textures</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">San Mateo Building Considerations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Climate-Appropriate Design</h3>
              <p className="text-muted-foreground">
                Modern Farmhouse designs adapt well to San Mateo's Mediterranean climate, with covered porches and outdoor spaces extending living areas.
              </p>
            </div>
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Material Selection</h3>
              <p className="text-muted-foreground">
                Durable, low-maintenance materials ensure longevity in San Mateo's coastal climate while maintaining authentic farmhouse aesthetics.
              </p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-foreground">$250,000 - $410,000</p>
            <p className="text-muted-foreground">
              Modern Farmhouse ADUs offer excellent value through efficient design and popular styling. Costs vary based on size and finish selections.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-foreground">Start Your Modern Farmhouse ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to add rustic charm with modern comfort to your San Mateo property? Let's design your perfect farmhouse ADU.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>

        {/* Internal Links */}
        <section className="text-center">
          <h3 className="text-2xl font-semibold mb-6 text-foreground">Explore Other ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="outline">
              <Link to="/contemporary-adu-design-san-mateo">Contemporary ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/craftsman-adu-design-san-mateo">Craftsman ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/ranch-style-adu-design-san-mateo">Ranch Style ADUs</Link>
            </Button>
          </div>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default ModernFarmhouseADUDesignSanMateo;